/**
 * "cards-id" listing variation — the tabular document list used across all
 * three archives (as a listing block and as the search block's result template
 * via listingBodyTemplate). Volto-19 port of the legacy ListingTableVariation,
 * reading the same configurable `fields` (already normalized to the unified
 * schema by normalizeBlockData) and rendered as the design system's
 * `.prc-table` (CONTRACT B) — styled globally via ds-core.css.
 *
 * Per-column sort (CONTRACT B): every column whose data is backed by a
 * sortable catalog index is clickable to sort asc/desc. `setSort` is threaded
 * in from the shadowed SearchBlockView -> ListingBody. A column is sortable
 * ONLY when a real sortable index backs it — Plone's catalog silently ignores
 * `sort_on` for ZCText/Keyword indexes, so showing a sort control there would
 * be a dead button (the exact "sorting not working" complaint). Title has no
 * sortable text index, so it sorts on `sortable_title`.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import downSVG from '@plone/volto/icons/sort-down.svg';
import upSVG from '@plone/volto/icons/sort-up.svg';
import updownSVG from '@plone/volto/icons/menu.svg';
import { isEmptyValue, renderValue } from '../../helpers/renderValue';
import useSiteConfig from '../../helpers/useSiteConfig';

/** the (possibly multi-name) field a column reads from -> its primary name. */
const fieldKey = (f) => (Array.isArray(f.field) ? f.field[0] : f.field);

/**
 * The first of `names` that `item` has a real value for, else undefined.
 *
 * Emptiness is `isEmptyValue`, the same predicate the renderer uses, so a
 * Plone unset-date sentinel (`1969-12-31`, the string `'None'`) counts as
 * absent and the next field gets its turn. Restating that rule here instead
 * would let the two drift, and the drift would show as a cell reading
 * "Dec 31, 1969" beside a column that had a real date available. (BL-0050
 * found those sentinels; BL-0057 is what made a second field reachable.)
 */
const firstValue = (item, names) => {
  for (const name of names) {
    if (!isEmptyValue(item[name])) return item[name];
  }
  return undefined;
};

/** primary field name / format -> CONTRACT B column `kind`. */
const columnKind = (f) => {
  const name = fieldKey(f);
  if (typeof name === 'string' && name.toLowerCase() === 'title') return 'title';
  if (name === 'docid') return 'docid';
  if (f.format === 'status') return 'status';
  return 'default';
};

/**
 * Field name -> catalog index to sort on when it differs from the field name.
 * (The metadata column and the sort index are not always the same object.)
 */
const SORT_ALIAS = {
  Title: 'sortable_title',
  title: 'sortable_title',
  Description: null, // ZCTextIndex — not sortable
  SearchableText: null,
  Subject: null, // KeywordIndex — not sortable
};

/**
 * Catalog indexes that can drive `sort_on` (FieldIndex / DateIndex /
 * DateRecurringIndex / BooleanIndex / UUIDIndex / GopipIndex). Sourced from the
 * live `portal_catalog` on the VM (2026-07-20). ZCText/Keyword/DateRange/Path
 * indexes are intentionally absent — the catalog no-ops sort_on on them.
 */
const SORTABLE_INDEXES = new Set([
  // FieldIndex
  'Creator', 'Type', 'acronym', 'cmf_uid', 'definition_type', 'delegant',
  'delegant_position', 'delegate', 'delegate_position', 'delegation_type',
  'docid', 'document_category', 'email', 'first_letter', 'getId', 'id',
  'in_reply_to', 'person_status', 'portal_type', 'process', 'project_status',
  'review_state', 'series', 'site_org', 'slm_org', 'sortable_title', 'status',
  'sync_uid',
  // DateIndex
  'Date', 'authority_effective_date', 'cancels_date', 'certifies_date',
  'created', 'date_signed', 'effective', 'expire_date', 'expires', 'issue_date',
  'modified', 'next_milestone', 'reaffirms_date', 'renumber_date',
  'rescinds_date', 'supersedes_date', 'updated_date',
  // DateRecurringIndex / UUIDIndex / GopipIndex
  'end', 'start', 'UID', 'getObjPositionInParent',
]);

/** the catalog index a column should sort on, or null if it can't sort. */
const resolveSortIndex = (f) => {
  // an explicit string `sortable` from the normalized field config wins.
  if (typeof f?.sortable === 'string' && f.sortable) return f.sortable;
  const name = fieldKey(f);
  if (!name) return null;
  if (Object.prototype.hasOwnProperty.call(SORT_ALIAS, name)) {
    return SORT_ALIAS[name];
  }
  return name;
};

const COLS_LS_KEY = 'prc-search-columns';
/** field-config -> its stable key (primary field name, else @id). */
const colKeyOf = (f) => fieldKey(f) || f['@id'];

/**
 * Solr's highlighter returns the matched text with `<mark>`…`</mark>` around
 * the hit and everything else VERBATIM — it does not HTML-escape the document
 * body. Feeding that to `dangerouslySetInnerHTML` would run whatever markup
 * happens to sit inside a PDF's text layer, so instead we split on the marker
 * tags and hand the pieces to React, which escapes every one of them. A literal
 * "<mark>" in a document is then just a highlighted stretch of text — the worst
 * case is a cosmetic one. (item 7)
 */
/**
 * Solr hands back the best-scoring 260-character window of the field, which for
 * a PDF whose TITLE contains the query is the cover page — so the snippet opens
 * with "NOT MEASUREMENT SENSITIVE / DISTRIBUTION STATEMENT A" boilerplate,
 * identically on every row, and the actual match sits below the fold of the
 * line. Drop the run-up when it is long enough to be doing that, keeping a
 * clause or so of lead-in for context. Purely presentational: the fragment
 * Solr chose is unchanged, it just starts where the answer is.
 */
const SNIPPET_LEAD = 70;

const trimToMatch = (text) => {
  const at = text.indexOf('<mark>');
  if (at <= SNIPPET_LEAD) return text;
  const wordBreak = text.lastIndexOf(' ', at - SNIPPET_LEAD);
  const cut = wordBreak > 0 ? wordBreak : at - SNIPPET_LEAD;
  return `…${text.slice(cut).trimStart()}`;
};

const renderSnippet = (text) =>
  trimToMatch(String(text))
    .split(/(<mark>|<\/mark>)/)
    .reduce(
      (acc, part) => {
        if (part === '<mark>') return { ...acc, on: true };
        if (part === '</mark>') return { ...acc, on: false };
        if (!part) return acc;
        acc.nodes.push(
          acc.on ? (
            <mark key={acc.nodes.length}>{part}</mark>
          ) : (
            <React.Fragment key={acc.nodes.length}>{part}</React.Fragment>
          ),
        );
        return acc;
      },
      { on: false, nodes: [] },
    ).nodes;

const TableListingView = (props) => {
  const {
    items = [],
    fields = [],
    sort_on,
    sort_order,
    setSort,
    searchMeta,
  } = props;
  const site = useSiteConfig();

  // The EFFECTIVE sort, which is not always the requested one. The search app
  // deliberately sends an empty `sort_on` so the backend can pick per intent
  // (relevance for a termed search, newest-first for an empty one) — leaving
  // the header carets showing "unsorted" while the rows were, in fact, sorted.
  // `search_meta.sort_on` is the backend saying what it actually did, and
  // 'relevance' means "no column sort", which is exactly the unsorted state.
  // (item 19)
  const metaSortOn =
    searchMeta?.sort_on && searchMeta.sort_on !== 'relevance'
      ? searchMeta.sort_on
      : '';
  const activeSortOn = sort_on || metaSortOn;
  const activeSortOrder = sort_order || searchMeta?.sort_order || 'descending';

  // AWS-table-style column picker: only when the block supplies a `columnCatalog`
  // (the Search App does). `fields` is the initial visible set; the catalog is
  // the full pickable list. Choices persist per-site via localStorage (each
  // archive is its own origin, so one key is naturally site-scoped).
  const columnCatalog = Array.isArray(props.columnCatalog)
    ? props.columnCatalog
    : null;
  const hasPicker = !!columnCatalog && columnCatalog.length > 0;
  const defaultKeys = React.useMemo(
    () => fields.map(colKeyOf).filter(Boolean),
    [fields],
  );
  const [visibleKeys, setVisibleKeys] = React.useState(defaultKeys);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const pickerRef = React.useRef(null);

  // hydrate saved column choice after mount (client-only; SSR keeps defaults so
  // server + first client render match).
  React.useEffect(() => {
    if (!hasPicker) return;
    try {
      const raw = window.localStorage.getItem(COLS_LS_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (Array.isArray(saved) && saved.length) setVisibleKeys(saved);
    } catch (e) {
      /* ignore malformed/absent storage */
    }
  }, [hasPicker]);

  // close the picker on outside-click / Escape.
  React.useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    const onKey = (e) => e.key === 'Escape' && setPickerOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const toggleColumn = (k) => {
    setVisibleKeys((prev) => {
      const next = prev.includes(k)
        ? prev.filter((x) => x !== k)
        : [...prev, k];
      try {
        window.localStorage.setItem(COLS_LS_KEY, JSON.stringify(next));
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  };

  const isSortable = (f) => {
    if (typeof setSort !== 'function') return false;
    if (f?.sortable === false) return false; // explicit opt-out
    const on = resolveSortIndex(f);
    return !!on && SORTABLE_INDEXES.has(on);
  };

  const toggleSort = (f) => {
    const on = resolveSortIndex(f);
    if (!on) return;
    if (activeSortOn === on) {
      setSort(on, activeSortOrder === 'ascending' ? 'descending' : 'ascending');
    } else {
      setSort(on, 'ascending');
    }
  };

  if (!fields.length) {
    return (
      <div className="prc-table-wrap">
        <div className="prc-table__empty">No columns configured.</div>
      </div>
    );
  }

  // Effective columns: with a picker, the catalog entries whose key is selected
  // (kept in catalog order, not click order); empty selection falls back to the
  // defaults so the table is never blank. Without a picker, the passed `fields`.
  let effectiveFields = fields;
  if (hasPicker) {
    const picked = columnCatalog.filter((f) =>
      visibleKeys.includes(colKeyOf(f)),
    );
    effectiveFields = picked.length ? picked : fields;
  }

  // Any quotable text on the page turns off the zebra stripe (see
  // theme/resultRow.less): a snippet is its own <tr>, so alternating rows stop
  // lining up with records the moment one appears.
  const hasSnippets = items.some((it) => !!it?.snippet);

  // The badge and the Site column say the same thing. The column is on by
  // default but the picker can drop it, so the badge only appears when it has
  // to — otherwise every cross-archive row states its origin twice.
  const siteColumnShown = effectiveFields.some(
    (f) => fieldKey(f) === 'site_label' || fieldKey(f) === 'site',
  );

  const columns = effectiveFields.map((f, i) => ({
    key: fieldKey(f) || f['@id'] || String(i),
    label: f.title,
    kind: columnKind(f),
    align: f.align || 'left',
    width: f.width,
    field: f,
  }));

  return (
    <div className="prc-table-wrap">
      {hasPicker && (
        <div className="prc-table-toolbar">
          <div className="prc-colpicker" ref={pickerRef}>
            <button
              type="button"
              className={`prc-colpicker__btn${pickerOpen ? ' is-open' : ''}`}
              aria-haspopup="true"
              aria-expanded={pickerOpen}
              onClick={() => setPickerOpen((o) => !o)}
            >
              <span>Columns</span>
              <Icon name={downSVG} size="14px" />
            </button>
            {pickerOpen && (
              <div className="prc-colpicker__menu" role="group" aria-label="Show columns">
                <div className="prc-colpicker__label">Show columns</div>
                {columnCatalog.map((f) => {
                  const k = colKeyOf(f);
                  const checked = visibleKeys.includes(k);
                  return (
                    <label key={k} className="prc-colpicker__item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleColumn(k)}
                      />
                      <span>{f.title}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      <table className={`prc-table${hasSnippets ? ' has-snippets' : ''}`}>
        <thead>
          <tr>
            {columns.map((col, i) => {
              const f = col.field;
              const on = resolveSortIndex(f);
              const active = !!on && activeSortOn === on;
              const sortable = isSortable(f);
              return (
                <th
                  key={col.key || i}
                  data-align={col.align}
                  scope="col"
                  style={col.width ? { width: col.width } : undefined}
                  aria-sort={
                    sortable && active
                      ? activeSortOrder === 'ascending'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      className={`prc-table__sort${active ? ' is-active' : ''}`}
                      onClick={() => toggleSort(f)}
                      aria-label={`Sort by ${f.title}`}
                    >
                      <span>{col.label}</span>
                      <Icon
                        name={
                          active
                            ? activeSortOrder === 'ascending'
                              ? upSVG
                              : downSVG
                            : updownSVG
                        }
                        size="14px"
                      />
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        {items.length ? (
          <tbody>
            {items.map((item, idx) => {
              const url = flattenToAppURL(item['@id'] || '');
              // Cross-site (other-archive) hits carry an ABSOLUTE URL that
              // flattenToAppURL leaves intact; a react-router <Link> would treat
              // it as an in-app path and emit `/https://...`. Render those as a
              // plain external anchor (new tab); same-site paths keep <Link>.
              const ext = /^https?:\/\//i.test(url);
              const linkCell = (className, children) =>
                ext ? (
                  <a
                    className={className}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ) : (
                  <Link className={className} to={url}>
                    {children}
                  </Link>
                );
              // Cross-archive result (item 20). Every hit now carries `site`;
              // a value other than the archive being browsed means the row came
              // from a sibling archive and its link leaves this domain. Badging
              // it is the difference between "surprising redirect" and
              // "deliberately searched everything".
              const foreign =
                !!item.site &&
                !!site?.id &&
                item.site !== site.id &&
                !siteColumnShown;
              const badge = foreign ? (
                <span className="prc-table__site-badge" title={`Result from the ${item.site_label || item.site} archive`}>
                  {item.site_label || item.site}
                </span>
              ) : null;
              return (
                <React.Fragment key={item['@id'] || item.UID || idx}>
                <tr className={foreign ? 'is-foreign' : undefined}>
                  {columns.map((col, i) => {
                    const f = col.field;
                    // multi-field cells stack lines (legacy [Delegate,
                    // DelegatePos]) UNLESS the column asks to resolve them --
                    // `resolve: 'first'` renders the first field that has a
                    // value and nothing at all when none do. The search app's
                    // date column uses it: `issue_date` for the 2,775 rows that
                    // carry one, `authority_effective_date` for the 1,037
                    // delegations, blank for a glossary entry that genuinely
                    // has no date (BL-0057).
                    const value = Array.isArray(f.field) ? (
                      f.resolve === 'first' ? (
                        renderValue(firstValue(item, f.field), f.format || 'auto')
                      ) : (
                        f.field.map((name) => (
                          <div key={name}>{renderValue(item[name], f.format || 'auto')}</div>
                        ))
                      )
                    ) : (
                      renderValue(item[f.field], f.format || 'auto')
                    );

                    let cell;
                    if (col.kind === 'title') {
                      const titled = url ? (
                        linkCell('prc-table__title', value)
                      ) : (
                        <span className="prc-table__title">{value}</span>
                      );
                      cell = badge ? (
                        <>
                          {titled}
                          {badge}
                        </>
                      ) : (
                        titled
                      );
                    } else if (col.kind === 'docid') {
                      // Rows without a document number (glossary entries) must
                      // not produce an empty focusable link with no name.
                      const hasVal =
                        item[f.field] != null && item[f.field] !== '';
                      const docid = <span className="prc-table__docid">{value}</span>;
                      // getObjSize is a stock catalog metadata column
                      // (metadata_fields='_all' always requests it) that reads
                      // "0 KB" for fileless content and is simply absent on
                      // Solr-sourced cross-archive rows -- both cases mean
                      // "no file to offer", and only the file link is dropped.
                      const hasFile =
                        typeof item.getObjSize === 'string' &&
                        parseFloat(item.getObjSize) > 0;
                      // The document number addresses the document PAGE, and
                      // the file gets a small labelled link of its own.
                      //
                      // The number ("ACD 130.1", "SD 452.2C") is the exact
                      // string this archive's searchers type, and this is the
                      // row's FIRST link -- so it is the anchor most likely to
                      // be attributed to whatever it points at. Until now it
                      // pointed at the PDF. On 2026-08-24 that was noticed and
                      // `nofollow` was added, which withdrew the endorsement
                      // without giving it to anything: the document page
                      // received the number as anchor text from nowhere on the
                      // site, and its only `referringUrls` in Search Console
                      // were sitemap files. Hence NNSA's parents sitting at
                      // "Discovered - currently not indexed" while their own
                      // PDFs rank -- sd-4522c is the worked example, and it is
                      // the single biggest PDF earner on the archive.
                      //
                      // So the number now links to the page. The file keeps a
                      // `nofollow` link beside it: reachable in one click for
                      // anyone who wants the PDF, but not competing for the
                      // term. Nothing is removed from the index and no URL
                      // stops resolving -- this only moves anchor text.
                      //
                      // See BL-0078.
                      const numbered =
                        f.link && url && hasVal ? (
                          linkCell('prc-table__docid-link', docid)
                        ) : (
                          docid
                        );
                      // Screen readers list links out of context, so every
                      // "PDF" on a 50-row page would otherwise share one
                      // accessible name. Name each after its own document.
                      const rawId = Array.isArray(f.field)
                        ? firstValue(item, f.field)
                        : item[f.field];
                      cell =
                        f.link && url && hasVal && hasFile ? (
                          <>
                            {numbered}
                            <a
                              className="prc-table__docid-file"
                              href={`${url}/@@display-file/file`}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              aria-label={
                                typeof rawId === 'string'
                                  ? `PDF of ${rawId}`
                                  : 'PDF of this document'
                              }
                            >
                              PDF
                            </a>
                          </>
                        ) : (
                          numbered
                        );
                    } else {
                      const hasVal = Array.isArray(f.field)
                        ? f.field.some((name) => item[name])
                        : item[f.field] != null && item[f.field] !== '';
                      cell = f.link && url && hasVal
                        ? linkCell(undefined, value)
                        : <span className="prc-cellvalue">{value}</span>;
                    }

                    return (
                      <td
                        key={col.key || i}
                        data-align={col.align}
                        data-label={col.label}
                        data-title={col.kind === 'title' ? '' : undefined}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
                {/* The matched text, straight from Solr's highlighter (item 7).
                    A second row spanning the table rather than a cell inside the
                    first: the snippet is about the ROW, and squeezing prose into
                    one of eight columns is what made the legacy result list
                    unreadable. Rows without a snippet (a title-only match, or a
                    browse listing) render nothing extra, so the table keeps its
                    tabular shape when there is nothing to quote. */}
                {item.snippet ? (
                  <tr className="prc-table__snippet-row">
                    <td colSpan={columns.length}>
                      <span className="prc-table__snippet">
                        {renderSnippet(item.snippet)}
                      </span>
                    </td>
                  </tr>
                ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        ) : null}
      </table>
      {!items.length && <div className="prc-table__empty">No documents found.</div>}
    </div>
  );
};

export default TableListingView;
